const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5025/api';

const getWsBaseUrl = () => {
  const customWs = process.env.REACT_APP_WS_URL;
  if (customWs) {
    const trimmed = customWs.replace(/\/+$/, '');
    return trimmed.endsWith('/ws/games') ? trimmed : `${trimmed}/ws/games`;
  }
  const customApi = process.env.REACT_APP_API_URL;
  if (customApi) {
    const trimmed = customApi.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
    return `${trimmed}/ws/games`;
  }
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname === 'localhost' ? 'localhost:5025' : window.location.host;
    return `${protocol}//${host}/ws/games`;
  }
  return 'ws://localhost:5025/ws/games';
};

class GameRealtimeClient {
  constructor() {
    this.gameListeners = new Map(); // Store WebSocket connections
    this.gameCallbacks = new Map(); // Store callbacks for each game
    this.statusListeners = new Set(); // Global connection status subscribers
    this.globalMessageListeners = new Set(); // Global message subscribers (e.g. social challenges)
    this.identifiedUser = null; // { userId, token }
    this.ws = null; // WebSocket connection
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1500;
    this.isConnecting = false;
    this.sri = Math.random().toString(36).substring(2, 10);
    this.connectionState = 'connected'; // 'connected' | 'reconnecting' | 'offline' | 'reconnected'
    
    // Heartbeat & Ack Queue (Lila style)
    this.pingTimer = null;
    this.pongTimeoutTimer = null;
    this.pendingMoveQueue = []; // [{ gameId, move, ackId, timestamp }]
    this.hasConnectedOnce = false;

    this.currentPing = 35; // Estimated RTT in ms
    this.lastPingSentAt = null;

    // Monitor browser online/offline status and page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleNetworkChange());
      window.addEventListener('offline', () => this.handleNetworkChange());
      window.addEventListener('beforeunload', () => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          try {
            this.ws.close(1001, 'Page navigating away');
          } catch (err) {}
        }
      });
    }
  }

  getPing() {
    return this.currentPing;
  }

  addStatusListener(callback) {
    this.statusListeners.add(callback);
    callback(this.connectionState, { isOnline: this.isOnline(), ping: this.currentPing, sri: this.sri });
    return () => this.statusListeners.delete(callback);
  }

  addGlobalMessageListener(callback) {
    this.globalMessageListeners.add(callback);
    return () => this.globalMessageListeners.delete(callback);
  }

  notifyGlobalMessageListeners(data) {
    this.globalMessageListeners.forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error('Global message listener error:', err);
      }
    });
  }

  identify(userId, token) {
    if (!userId) {
      this.identifiedUser = null;
      return;
    }
    const authToken = token || localStorage.getItem('token');
    this.identifiedUser = { userId: String(userId), token: authToken };
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'identify',
        userId: String(userId),
        token: authToken
      }));
    } else {
      this.connectWebSocket().catch(console.error);
    }
  }

  notifyStatusListeners(state, extraInfo = {}) {
    this.connectionState = state;
    const info = { isOnline: this.isOnline(), ping: this.currentPing, sri: this.sri, ...extraInfo };
    this.statusListeners.forEach((cb) => {
      try {
        cb(state, info);
      } catch (err) {
        console.error('Status listener error:', err);
      }
    });
  }

  isOnline() {
    return typeof navigator === 'undefined' || !('onLine' in navigator) || navigator.onLine;
  }

  handleNetworkChange() {
    if (this.intentionalDisconnect) return; // don't reconnect after logout
    if (!this.isOnline()) {
      this.notifyStatusListeners('offline');
    } else {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.notifyStatusListeners('reconnecting');
        this.connectWebSocket().catch(console.error);
      }
    }
  }

  // API Methods (for initial game creation and joining)
  async makeApiRequest(endpoint, options = {}) {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }
    
    const url = `${API_BASE_URL}/online-games${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      ...options
    };

    const response = await fetch(url, config);
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`API endpoint not found: ${url}`);
    }
    
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  }

  // Create a new game
  async createGame(gameSettings = {}) {
    return this.makeApiRequest('/create', {
      method: 'POST',
      body: JSON.stringify(gameSettings)
    });
  }

  // Join a game
  async joinGame(gameId) {
    return this.makeApiRequest(`/${gameId}/join`, {
      method: 'POST'
    });
  }

  // Cancel a waiting game
  async cancelGame(gameId) {
    return this.makeApiRequest(`/${gameId}/cancel`, {
      method: 'POST'
    });
  }

  // Get game by ID (one-time fetch)
  async getGame(gameId) {
    return this.makeApiRequest(`/${gameId}`);
  }

  // WebSocket-based real-time updates for game
  subscribeToGame(gameId, callback) {
    console.log('Using WebSocket for real-time game updates');
    
    if (!this.gameCallbacks.has(gameId)) {
      this.gameCallbacks.set(gameId, new Set());
    }
    this.gameCallbacks.get(gameId).add(callback);

    this.connectWebSocket().then(() => {
      this.subscribeToGameWebSocket(gameId);
    }).catch(error => {
      console.error('Failed to connect WebSocket:', error);
      this.notifyGameCallbacks(gameId, 'error', { 
        error: 'WebSocket connection failed. Please refresh the page.' 
      });
    });
  }

  // Connect to WebSocket server with Heartbeat & Reconnect Management
  async connectWebSocket() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return new Promise((resolve, reject) => {
        const checkConnection = () => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            resolve();
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    return new Promise((resolve, reject) => {
      this.isConnecting = true;
      if (this.hasConnectedOnce) {
        this.notifyStatusListeners('reconnecting');
      }
      
      try {
        const wsBase = getWsBaseUrl();
        const token = this.identifiedUser?.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
        const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
        const wsUrl = `${wsBase}?sri=${this.sri}${tokenParam}`;
        console.log('🔗 [GameRealtimeClient] Connecting to WebSocket:', wsBase);
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1500;
          this.intentionalDisconnect = false; // clear so normal reconnect works after re-login

          // Re-identify if user state was set
          if (this.identifiedUser?.userId) {
            this.ws.send(JSON.stringify({
              type: 'identify',
              userId: this.identifiedUser.userId,
              token: this.identifiedUser.token
            }));
          }

          if (this.hasConnectedOnce) {
            this.notifyStatusListeners('reconnected');
            setTimeout(() => {
              if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.notifyStatusListeners('connected');
              }
            }, 2500);
          } else {
            this.hasConnectedOnce = true;
            this.notifyStatusListeners('connected');
          }

          // Start ping heartbeat & resubmit subscriptions / buffered moves
          this.startHeartbeat();
          this.resubscribeAllGames();
          this.flushPendingMoveQueue();

          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleWebSocketMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          this.isConnecting = false;
          this.stopHeartbeat();
          this.ws = null;
          
          if (!this.isOnline()) {
            this.notifyStatusListeners('offline');
          } else {
            this.notifyStatusListeners('reconnecting');
          }

          if (event.code !== 1000) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          if (!this.isOnline()) {
            this.notifyStatusListeners('offline');
          } else {
            this.notifyStatusListeners('reconnecting');
          }
          reject(error);
        };

        setTimeout(() => {
          if (this.isConnecting) {
            this.isConnecting = false;
            this.ws?.close();
            this.notifyStatusListeners('reconnecting');
            reject(new Error('WebSocket connection timeout'));
          }
        }, 10000);

      } catch (error) {
        this.isConnecting = false;
        if (!this.isOnline()) {
          this.notifyStatusListeners('offline');
        } else {
          this.notifyStatusListeners('reconnecting');
        }
        reject(error);
      }
    });
  }

  startHeartbeat() {
    this.stopHeartbeat();
    const sendPing = () => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.lastPingSentAt = Date.now();
        this.ws.send(JSON.stringify({ type: 'ping', sri: this.sri, t: this.lastPingSentAt }));
        
        // Schedule pong timeout (10 seconds threshold)
        clearTimeout(this.pongTimeoutTimer);
        this.pongTimeoutTimer = setTimeout(() => {
          console.warn('WebSocket pong timeout reached, closing socket for reconnect...');
          if (this.ws) {
            this.ws.close();
          }
        }, 10000);
      }
    };

    // Send immediate ping then repeat every 8 seconds
    sendPing();
    this.pingTimer = setInterval(sendPing, 8000);
  }

  stopHeartbeat() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.pongTimeoutTimer) clearTimeout(this.pongTimeoutTimer);
    this.pingTimer = null;
    this.pongTimeoutTimer = null;
  }

  handlePong(message) {
    clearTimeout(this.pongTimeoutTimer);
    const now = Date.now();
    const rtt = (message && message.t) 
      ? (now - message.t) 
      : (this.lastPingSentAt ? (now - this.lastPingSentAt) : 35);
    this.currentPing = Math.max(5, Math.min(2000, rtt));
    this.notifyStatusListeners(this.connectionState, { ping: this.currentPing });
  }

  resubscribeAllGames() {
    this.gameCallbacks.forEach((callbacks, gameId) => {
      if (callbacks.size > 0) {
        this.subscribeToGameWebSocket(gameId);
        // Trigger game resync via HTTP to recover state after reconnect
        this.resyncGame(gameId);
      }
    });
  }

  async resyncGame(gameId) {
    try {
      const latestState = await this.getGame(gameId);
      this.notifyGameCallbacks(gameId, 'game-state', latestState);
    } catch (err) {
      console.error(`Failed to resync game ${gameId}:`, err);
    }
  }

  // Schedule WebSocket reconnection with exponential backoff and randomized jitter
  scheduleReconnect() {
    if (this.intentionalDisconnect) return; // don't reconnect after logout

    this.reconnectAttempts++;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (!this.isOnline()) {
      this.notifyStatusListeners('offline');
    } else {
      this.notifyStatusListeners('reconnecting');
    }
    const baseDelay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1), 15000);
    const jitter = Math.floor(Math.random() * 1500);
    const delay = baseDelay + jitter;

    console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts} in ${delay}ms (base: ${baseDelay}ms, jitter: ${jitter}ms)`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.intentionalDisconnect && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
        this.connectWebSocket().catch(error => {
          console.error('WebSocket reconnection failed:', error);
          this.notifyStatusListeners('reconnecting');
        });
      }
    }, delay);
  }

  // Handle WebSocket messages
  handleWebSocketMessage(message) {
    const { type, gameId, data, ackId } = message;

    switch (type) {
      case 'connected':
        console.log('WebSocket connection confirmed');
        break;

      case 'pong':
        this.handlePong(message);
        break;

      case 'ack':
        if (ackId) {
          this.pendingMoveQueue = this.pendingMoveQueue.filter(item => item.ackId !== ackId);
        }
        break;
      
      case 'game-state':
        this.notifyGameCallbacks(gameId, 'game-state', data);
        break;

      case 'move-delta':
        this.notifyGameCallbacks(gameId, 'move-delta', message);
        break;

      case 'move-rejected':
        this.notifyGameCallbacks(gameId, 'move-rejected', message);
        break;

      case 'player-disconnected':
        this.notifyGameCallbacks(gameId, 'player-disconnected', data);
        break;

      case 'player-reconnected':
        this.notifyGameCallbacks(gameId, 'player-reconnected', data);
        break;
      
      case 'game-not-found':
        this.notifyGameCallbacks(gameId, 'game-not-found', { gameId });
        break;

      case 'resync':
        this.resyncGame(gameId);
        break;
      
      case 'error':
        console.error('WebSocket error message:', message);
        this.notifyGameCallbacks(gameId, 'error', { error: message.message });
        break;
      
      case 'subscribed':
        console.log(`Successfully subscribed to game ${gameId} via WebSocket`);
        break;
      
      case 'unsubscribed':
        console.log(`Successfully unsubscribed from game ${gameId} via WebSocket`);
        break;

      case 'timer-sync':
        console.log('Ignoring legacy timer-sync message');
        break;

      case 'identified':
      case 'connection':
        // Connection & User identification acknowledgment from WebSocket server
        break;
      
      case 'challenge:created':
      case 'challenge:accepted':
      case 'challenge:declined':
      case 'challenge:canceled':
      case 'friends:updated':
      case 'waiting-games-updated':
      case 'user-games-updated':
        this.notifyGlobalMessageListeners(message);
        break;

      default:
        // Forward any non-game or social events to global subscribers as fallback
        if (type && (type.startsWith('challenge:') || type.startsWith('friends:'))) {
          this.notifyGlobalMessageListeners(message);
        } else {
          console.warn('Unknown WebSocket message type:', type);
        }
    }
  }

  // Subscribe to game via WebSocket
  subscribeToGameWebSocket(gameId) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    const token = localStorage.getItem('token');
    const userId = token ? JSON.parse(atob(token.split('.')[1])).userId?.toString() : null;

    this.ws.send(JSON.stringify({
      type: 'subscribe',
      gameId,
      userId,
      token
    }));

    this.gameListeners.set(gameId, () => {
      this.unsubscribeFromGameWebSocket(gameId);
    });
  }

  // Unsubscribe from game via WebSocket
  unsubscribeFromGameWebSocket(gameId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const token = localStorage.getItem('token');
      const userId = token ? JSON.parse(atob(token.split('.')[1])).userId?.toString() : null;

      this.ws.send(JSON.stringify({
        type: 'unsubscribe',
        gameId,
        userId
      }));
    }

    this.gameListeners.delete(gameId);
  }

  fallbackToPollingForAllGames() {
    console.log('Polling fallback is DISABLED - notifying clients of connection issues');
    
    this.gameCallbacks.forEach((callbacks, gameId) => {
      this.notifyGameCallbacks(gameId, 'error', { 
        error: 'Connection lost. Please refresh the page to reconnect.' 
      });
    });
  }

  startPolling(gameId, callback) {
    console.log('POLLING IS DISABLED - Use WebSocket only');
    
    this.notifyGameCallbacks(gameId, 'error', { 
      error: 'Polling is disabled. Please refresh the page.' 
    });
    
    return;
  }

  // Remove game listener
  unsubscribeFromGame(gameId, callback) {
    if (this.gameCallbacks.has(gameId)) {
      this.gameCallbacks.get(gameId).delete(callback);
      
      if (this.gameCallbacks.get(gameId).size === 0) {
        this.gameCallbacks.delete(gameId);
        
        if (this.gameListeners.has(gameId)) {
          const unsubscribe = this.gameListeners.get(gameId);
          unsubscribe();
          this.gameListeners.delete(gameId);
        }
      }
    }
  }

  // Notify all callbacks for a game
  notifyGameCallbacks(gameId, event, data) {
    if (this.gameCallbacks.has(gameId)) {
      this.gameCallbacks.get(gameId).forEach(callback => {
        callback(event, data);
      });
    }
  }

  // Flush buffered moves played during offline/reconnecting state
  async flushPendingMoveQueue() {
    if (this.pendingMoveQueue.length === 0) return;

    console.log(`Flushing ${this.pendingMoveQueue.length} pending offline moves...`);
    const queueToFlush = [...this.pendingMoveQueue];
    
    for (const item of queueToFlush) {
      try {
        await this.makeApiRequest(`/${item.gameId}/move`, {
          method: 'POST',
          body: JSON.stringify({ move: item.move, ackId: item.ackId })
        });
        // Remove from queue on success
        this.pendingMoveQueue = this.pendingMoveQueue.filter(m => m.ackId !== item.ackId);
      } catch (err) {
        console.error(`Failed to flush pending move for game ${item.gameId}:`, err);
      }
    }
  }

  // Make a move with direct WebSocket primary transport and offline fallback queuing
  async makeMove(gameId, move) {
    const ackId = Math.random().toString(36).substring(2, 9);
    const movePayload = { move, ackId };

    // Primary transport: direct WebSocket message (<5ms latency)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        const token = this.token || localStorage.getItem('token');
        this.ws.send(JSON.stringify({
          type: 'move',
          gameId,
          move,
          token,
          ackId
        }));
        return { success: true, ackId, transport: 'ws' };
      } catch (wsErr) {
        console.warn('Direct WebSocket move failed, falling back to HTTP:', wsErr);
      }
    }

    try {
      const response = await this.makeApiRequest(`/${gameId}/move`, {
        method: 'POST',
        body: JSON.stringify(movePayload)
      });
      return response;
    } catch (err) {
      console.warn(`Move submission failed (will queue for reconnect): ${err.message}`);
      
      // Buffer move locally if offline or socket disconnected
      this.pendingMoveQueue.push({
        gameId,
        move,
        ackId,
        timestamp: Date.now()
      });

      // Notify status listeners
      if (!this.isOnline()) {
        this.notifyStatusListeners('offline');
      } else {
        this.notifyStatusListeners('reconnecting');
      }

      return { queued: true, ackId };
    }
  }

  // Resign game using API
  async resignGame(gameId) {
    return this.makeApiRequest(`/${gameId}/resign`, {
      method: 'POST'
    });
  }

  // Abort game using API (only before first move in non-tournament match)
  async abortGame(gameId) {
    return this.makeApiRequest(`/${gameId}/abort`, {
      method: 'POST'
    });
  }

  // Offer draw using API
  async offerDraw(gameId) {
    return this.makeApiRequest(`/${gameId}/draw/offer`, {
      method: 'POST'
    });
  }

  // Accept draw using API
  async acceptDraw(gameId) {
    return this.makeApiRequest(`/${gameId}/draw/accept`, {
      method: 'POST'
    });
  }

  // Decline draw using API
  async declineDraw(gameId) {
    return this.makeApiRequest(`/${gameId}/draw/decline`, {
      method: 'POST'
    });
  }

  // Cancel draw using API
  async cancelDraw(gameId) {
    return this.makeApiRequest(`/${gameId}/draw/cancel`, {
      method: 'POST'
    });
  }

  // Request rematch using API
  async requestRematch(gameId) {
    return this.makeApiRequest(`/${gameId}/rematch/offer`, {
      method: 'POST'
    });
  }

  // Accept rematch using API
  async acceptRematch(gameId) {
    return this.makeApiRequest(`/${gameId}/rematch/accept`, {
      method: 'POST'
    });
  }

  // Decline rematch using API
  async declineRematch(gameId) {
    return this.makeApiRequest(`/${gameId}/rematch/decline`, {
      method: 'POST'
    });
  }

  // Cancel rematch using API
  async cancelRematch(gameId) {
    return this.makeApiRequest(`/${gameId}/rematch/cancel`, {
      method: 'POST'
    });
  }

  // Check timeout for a game (server-side timer validation)
  async checkTimeout(gameId) {
    try {
      return this.makeApiRequest(`/${gameId}/check-timeout`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error checking timeout:', error);
      return null;
    }
  }

  async updatePlayerTime(gameId, playerColor, timeRemaining) {
    console.log('Timer updates are now handled server-side through moves');
    return Promise.resolve();
  }

  // Update player connection status via API
  async updateConnectionStatus(gameId, connected) {
    try {
      return this.makeApiRequest(`/${gameId}/connection`, {
        method: 'POST',
        body: JSON.stringify({ connected })
      });
    } catch (error) {
      return Promise.resolve();
    }
  }

  // Send chat message via API
  async sendChatMessage(gameId, message) {
    try {
      return this.makeApiRequest(`/${gameId}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message })
      });
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  subscribeToChatMessages(gameId, callback) {
    console.warn('Chat polling not implemented - using API-only approach');
    callback('chat-messages', []);
    return () => {}; 
  }

  // Get waiting games
  async getWaitingGames() {
    return this.makeApiRequest('/waiting/list');
  }

  // Get user games
  async getUserGames(userId) {
    return this.makeApiRequest(`/user/${userId}`);
  }

  // Start a game
  async startGame(gameId) {
    return this.makeApiRequest(`/${gameId}/start`, {
      method: 'POST'
    });
  }

  // Friend game invitation methods
  async sendGameInvitation(toUserId, gameSettings) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/friend-games/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ toUserId, gameSettings })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send invitation');
    }
    return data;
  }

  async acceptInvitation(invitationId) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/friend-games/invite/${invitationId}/accept`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to accept invitation');
    }
    return data;
  }

  async declineInvitation(invitationId) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/friend-games/invite/${invitationId}/decline`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to decline invitation');
    }
    return data;
  }

  async getReceivedInvitations() {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/friend-games/invitations/received`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch invitations');
    }
    return data;
  }

  // Check if a game is using WebSocket
  isUsingWebSocket(gameId) {
    return this.ws && this.ws.readyState === WebSocket.OPEN && this.gameListeners.has(gameId);
  }

  // Get connection status for debugging
  getConnectionStatus() {
    return {
      usingWebSocket: this.ws && this.ws.readyState === WebSocket.OPEN,
      webSocketState: this.ws ? this.ws.readyState : 'Not connected',
      reconnectAttempts: this.reconnectAttempts,
      activeListeners: this.gameListeners.size,
      activeCallbacks: Array.from(this.gameCallbacks.entries()).map(([gameId, callbacks]) => ({
        gameId,
        callbackCount: callbacks.size
      }))
    };
  }

  // Cleanup all listeners and close WebSocket — call on logout to stop all reconnect attempts
  disconnect() {
    // Mark as intentionally disconnected — suppresses scheduleReconnect and handleNetworkChange
    this.intentionalDisconnect = true;

    this.stopHeartbeat();

    // Cancel any pending reconnect timer so it doesn't fire after logout
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      try {
        this.ws.close(1000, 'Client disconnecting');
      } catch (err) {}
      this.ws = null;
    }

    this.gameListeners.forEach((stopFunction) => {
      if (typeof stopFunction === 'function') {
        stopFunction();
      }
    });

    this.gameListeners.clear();
    this.gameCallbacks.clear();
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.hasConnectedOnce = false; // reset so next login starts fresh without "reconnecting" flash
    this.pendingMoveQueue = [];

    // Push 'connected' so the banner hides immediately after logout
    this.notifyStatusListeners('connected');
  }

  // Send heartbeat ping
  ping() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }
}

const instance = new GameRealtimeClient();
export default instance;
